import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker } from "react-router";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  useBrandingQuery,
  useUpdateBrandingMutation,
  usePublishBrandingMutation,
  useRevertBrandingDraftMutation,
} from "../../../apollo/branding";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { ImagePlus, X, Check } from "lucide-react";
import { HexColorPicker } from "react-colorful";
import { BrandingPreview, EmailPreview } from "./BrandingPreview";
import { THEME_PRESETS, type ThemePreset } from "./themePresets";
import { FONT_STACKS, getFontCss, getFontUrl } from "./fontStacks";

// ── Alternate logos + email branding (stored on tenants table) ───

// brandName is the only branding-ish field still on the tenants table. The
// footer/email logos and email color overrides moved onto tenant_branding in
// V113 so the whole Branding page shares one draft/publish lifecycle -- they
// now go through updateBranding like everything else.
const GET_TENANT_BRANDING_EXTRAS = gql`
  query TenantBrandingExtras {
    currentTenant {
      id
      brandName
    }
  }
`;

const UPDATE_TENANT_BRAND_NAME = gql`
  mutation UpdateTenantBrandName($input: TenantConfigInput!) {
    updateTenantConfig(input: $input) {
      id
      brandName
    }
  }
`;

// ── Types ───────────────────────────────────────────────────────

interface ColorPalette {
  primaryColor: string | null;
  secondaryColor: string | null;
  tertiaryColor: string | null;
  backgroundColor: string | null;
  primaryBgColor: string | null;
  secondaryBgColor: string | null;
  tertiaryBgColor: string | null;
}

const PALETTE_KEYS: (keyof ColorPalette)[] = [
  "primaryColor",
  "secondaryColor",
  "tertiaryColor",
  "backgroundColor",
  "primaryBgColor",
  "secondaryBgColor",
  "tertiaryBgColor",
];

function palettesEqual(a: ColorPalette, b: ColorPalette): boolean {
  return PALETTE_KEYS.every((k) => (a[k] || null) === (b[k] || null));
}

function extractPalette(source: {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  tertiaryColor?: string | null;
  backgroundColor?: string | null;
  primaryBgColor?: string | null;
  secondaryBgColor?: string | null;
  tertiaryBgColor?: string | null;
} | null | undefined): ColorPalette {
  return {
    primaryColor: source?.primaryColor || null,
    secondaryColor: source?.secondaryColor || null,
    tertiaryColor: source?.tertiaryColor || null,
    backgroundColor: source?.backgroundColor || null,
    primaryBgColor: source?.primaryBgColor || null,
    secondaryBgColor: source?.secondaryBgColor || null,
    tertiaryBgColor: source?.tertiaryBgColor || null,
  };
}

/** Email branding override object shape (all fields optional). */
interface EmailBranding {
  headerBgColor?: string | null;
  accentColor?: string | null;
  highlightBgColor?: string | null;
  calloutBgColor?: string | null;
}

type ActiveTab = "website" | "email";

// ── Helpers ─────────────────────────────────────────────────────

function isValidHex(color: string): boolean {
  const hex = color.trim().replace("#", "");
  return /^[0-9A-Fa-f]{6}$/.test(hex);
}

function normalizeHex(color: string): string {
  let hex = color.trim().replace("#", "");
  if (hex.length === 3) {
    hex = hex.split("").map((c) => c + c).join("");
  }
  return "#" + hex.toUpperCase();
}

// ── Logo Input Component ─────────────────────────────────────

interface LogoInputProps {
  logoUrl: string | null;
  onUpdate: (url: string | null) => void;
}

function BusinessNameInput({ brandName }: { brandName: string | null }) {
  const [value, setValue] = useState(brandName ?? "");
  const [saved, setSaved] = useState(false);
  const [mutate, { loading, error }] = useMutation(UPDATE_TENANT_BRAND_NAME);

  useEffect(() => {
    setValue(brandName ?? "");
  }, [brandName]);

  const dirty = value.trim() !== (brandName ?? "");

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed || !dirty) return;
    await mutate({ variables: { input: { brandName: trimmed } } });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
          }}
          maxLength={80}
          placeholder="Your business name"
          className="w-full max-w-sm rounded-[9px] border border-ink-6 bg-paper px-[11px] py-2 text-[13px] text-ink transition-all focus:border-tide focus:ring-[3px] focus:ring-tide-s focus:outline-none"
        />
        <button
          onClick={() => void save()}
          disabled={!dirty || !value.trim() || loading}
          className="rounded-md bg-tide px-3.5 py-2 text-[12.5px] font-semibold text-white transition-opacity disabled:opacity-40"
        >
          {loading ? "Saving..." : saved ? "Saved" : "Save"}
        </button>
      </div>
      {error && (
        <div className="mt-2 text-[11.5px] text-rust">{error.message}</div>
      )}
    </div>
  );
}

function LogoInput({ logoUrl, onUpdate }: LogoInputProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("asset_type", "logo");

      const res = await fetch("/api/branding/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        if (res.status === 422) {
          const body = (await res.json().catch(() => null)) as
            | { error?: string }
            | null;
          throw new Error(body?.error ?? "Invalid file type or size");
        }
        throw new Error("Upload failed");
      }

      const data = (await res.json()) as { cdn_url: string };
      onUpdate(data.cdn_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemove = () => {
    onUpdate(null);
  };

  return (
    <div>
      {logoUrl ? (
        <div className="hair relative overflow-hidden rounded-md border bg-paper">
          <img
            src={logoUrl}
            alt="Logo"
            className="block max-h-44 w-full object-contain"
          />
          <button
            type="button"
            onClick={handleRemove}
            aria-label="Remove logo"
            className="absolute right-2 top-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-paper-hi/95 text-ink-3 shadow-md hover:text-rust"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      ) : (
        <label
          className={`hair flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed bg-paper px-4 py-8 text-center transition hover:bg-paper-2 ${
            uploading ? "opacity-60" : ""
          }`}
        >
          <ImagePlus size={20} strokeWidth={1.6} className="text-ink-4" />
          <div className="text-[12.5px] font-medium text-ink-3">
            {uploading ? "Uploading..." : "Upload logo"}
          </div>
          <div className="text-[11px] text-ink-5">PNG, SVG, or JPEG - 2 MB max</div>
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      )}
      {error && <div className="mt-2 text-[11.5px] text-rust">{error}</div>}
    </div>
  );
}

// ── Alternate Logo Input Component ──────────────────────────────

interface AlternateLogoInputProps {
  imageUrl: string | null;
  assetType: string;
  label: string;
  onUpdate: (url: string | null) => void;
}

function AlternateLogoInput({ imageUrl, assetType, label, onUpdate }: AlternateLogoInputProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("asset_type", assetType);

      const res = await fetch("/api/branding/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        if (res.status === 422) {
          const body = (await res.json().catch(() => null)) as
            | { error?: string }
            | null;
          throw new Error(body?.error ?? "Invalid file type or size");
        }
        throw new Error("Upload failed");
      }

      const data = (await res.json()) as { cdn_url: string };
      onUpdate(data.cdn_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemove = () => {
    onUpdate(null);
  };

  return (
    <div>
      {imageUrl ? (
        <div className="hair relative overflow-hidden rounded-md border bg-paper">
          <img
            src={imageUrl}
            alt={label}
            className="block max-h-44 w-full object-contain"
          />
          <button
            type="button"
            onClick={handleRemove}
            aria-label={`Remove ${label.toLowerCase()}`}
            className="absolute right-2 top-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-paper-hi/95 text-ink-3 shadow-md hover:text-rust"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      ) : (
        <label
          className={`hair flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed bg-paper px-4 py-8 text-center transition hover:bg-paper-2 ${
            uploading ? "opacity-60" : ""
          }`}
        >
          <ImagePlus size={20} strokeWidth={1.6} className="text-ink-4" />
          <div className="text-[12.5px] font-medium text-ink-3">
            {uploading ? "Uploading..." : `Upload ${label.toLowerCase()}`}
          </div>
          <div className="text-[11px] text-ink-5">PNG, SVG, or JPEG - 2 MB max</div>
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      )}
      {error && <div className="mt-2 text-[11.5px] text-rust">{error}</div>}
    </div>
  );
}

// ── Favicon Input Component ────────────────────────────────────

interface FaviconInputProps {
  faviconUrl: string | null;
  onUpdate: (url: string | null) => void;
}

function FaviconInput({ faviconUrl, onUpdate }: FaviconInputProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("asset_type", "favicon");

      const res = await fetch("/api/branding/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        if (res.status === 422) {
          const body = (await res.json().catch(() => null)) as
            | { error?: string }
            | null;
          throw new Error(body?.error ?? "Invalid file type or size");
        }
        throw new Error("Upload failed");
      }

      const data = (await res.json()) as { cdn_url: string };
      onUpdate(data.cdn_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemove = () => {
    onUpdate(null);
  };

  return (
    <div>
      {faviconUrl ? (
        <div className="hair relative overflow-hidden rounded-md border bg-paper">
          <img
            src={faviconUrl}
            alt="Favicon"
            className="block max-h-44 w-full object-contain"
          />
          <button
            type="button"
            onClick={handleRemove}
            aria-label="Remove favicon"
            className="absolute right-2 top-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-paper-hi/95 text-ink-3 shadow-md hover:text-rust"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      ) : (
        <label
          className={`hair flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed bg-paper px-4 py-8 text-center transition hover:bg-paper-2 ${
            uploading ? "opacity-60" : ""
          }`}
        >
          <ImagePlus size={20} strokeWidth={1.6} className="text-ink-4" />
          <div className="text-[12.5px] font-medium text-ink-3">
            {uploading ? "Uploading..." : "Upload favicon"}
          </div>
          <div className="text-[11px] text-ink-5">PNG, SVG, or ICO - 500 KB max</div>
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      )}
      {error && <div className="mt-2 text-[11.5px] text-rust">{error}</div>}
    </div>
  );
}

// ── Color Picker Component ─────────────────────────────────────

interface ColorInputProps {
  value: string | null;
  onChange: (value: string) => void;
  label?: string;
}

function ColorInput({ value, onChange, label }: ColorInputProps) {
  const [color, setColor] = useState(value || "#2663C7");
  const [manualValue, setManualValue] = useState(value || "#2663C7");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Sync internal state when value prop changes externally
  // (e.g., theme preset applied or discard clicked)
  useEffect(() => {
    const v = value || "#2663C7";
    setColor(v);
    setManualValue(v);
  }, [value]);

  // Sync manual input when color changes from picker
  useEffect(() => {
    setManualValue(color);
  }, [color]);

  // Dismiss the popover on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    setManualValue(val);
    // Only update color if valid hex
    if (isValidHex(val)) {
      const normalized = normalizeHex(val);
      setColor(normalized);
      onChange(normalized);
    }
  };

  const handleColorChange = (newColor: string) => {
    setColor(newColor);
    setManualValue(newColor);
    onChange(newColor);
  };

  return (
    <div className="flex flex-col gap-2" ref={wrapRef}>
      {label && (
        <label className="text-[12.5px] font-medium text-ink-2">{label}</label>
      )}
      <div className="relative">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={label ? `Choose ${label.toLowerCase()}` : "Choose color"}
            aria-expanded={open}
            className="hair h-8 w-8 shrink-0 cursor-pointer rounded-md border shadow-sm transition hover:brightness-95"
            style={{ backgroundColor: color }}
          />
          <input
            type="text"
            value={manualValue}
            onChange={handleHexChange}
            onFocus={() => setOpen(false)}
            placeholder="#RRGGBB"
            aria-label={label ? `${label} hex value` : "Hex value"}
            className="w-full flex-1 rounded-md border px-2 py-1.5 font-mono text-[12.5px] text-ink focus:border-tide focus:outline-none"
          />
        </div>

        {open && (
          <div className="hair absolute left-0 top-[38px] z-40 rounded-lg border bg-paper-hi p-3 shadow-lift">
            <HexColorPicker color={color} onChange={handleColorChange} />
            <div className="mt-2 text-center font-mono text-[11px] text-ink-4">
              {color}
            </div>
          </div>
        )}
      </div>
      <div className="text-[11px] text-ink-5">
        6-digit hex color (e.g., #2663C7)
      </div>
    </div>
  );
}

// ── Inherit Toggle Component ──────────────────────────────────

interface InheritToggleProps {
  /** Label describing what value is inherited */
  inheritLabel: string;
  /** The site palette color being inherited */
  inheritedColor: string | null;
  /** Hardcoded default when even the site palette is unset */
  defaultColor: string;
  /** The current custom override value, or null if inheriting */
  customValue: string | null;
  /** Called with the new value (hex string) or null to clear override */
  onChange: (value: string | null) => void;
}

function InheritToggle({
  inheritLabel,
  inheritedColor,
  defaultColor,
  customValue,
  onChange,
}: InheritToggleProps) {
  const isInheriting = customValue == null;
  const displayColor = inheritedColor || defaultColor;

  return (
    <div className="mt-3 space-y-3">
      <button
        type="button"
        onClick={() => {
          if (isInheriting) {
            // Switch to custom: seed with the inherited value
            onChange(displayColor);
          } else {
            // Switch to inheriting: clear the override
            onChange(null);
          }
        }}
        className="hair flex w-full cursor-pointer items-center justify-between rounded-lg border border-dashed px-3 py-2.5 transition hover:bg-paper-2"
      >
        <span className="flex items-center gap-2 text-[12.5px] text-ink-3">
          <span>Use site {inheritLabel}</span>
          <span
            className="inline-block h-3 w-3 rounded-[3px] border border-ink-6/60"
            style={{ backgroundColor: displayColor }}
          />
          <span className="font-mono text-[10.5px] text-ink-5">
            {displayColor}
          </span>
        </span>
        <span
          className={`relative inline-block h-[18px] w-[32px] rounded-full transition-colors ${
            isInheriting ? "bg-tide" : "bg-ink-6"
          }`}
        >
          <span
            className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-[left] ${
              isInheriting ? "left-[16px]" : "left-[2px]"
            }`}
          />
        </span>
      </button>

      {!isInheriting && (
        <ColorInput
          value={customValue}
          onChange={(color) => onChange(color)}
        />
      )}
    </div>
  );
}

// ── Main Branding Page Component ───────────────────────────────

export function BrandingPage() {
  const { data, loading, error } = useBrandingQuery();
  const [updateBranding, { error: imageUpdateError }] =
    useUpdateBrandingMutation();

  // Tenant extras: alternate logos + email branding overrides
  const { data: tenantExtrasData } = useQuery(GET_TENANT_BRANDING_EXTRAS);
  const [publishBranding] = usePublishBrandingMutation();
  const [revertBrandingDraft] = useRevertBrandingDraftMutation();

  const branding = data?.branding;

  // ── Active tab ─────────────────────────────────────────────

  const [activeTab, setActiveTab] = useState<ActiveTab>("website");

  // ── Saved palette (from server) ────────────────────────────

  const savedPalette = useMemo<ColorPalette>(
    () => extractPalette(branding),
    [branding],
  );

  // ── Candidate palette (local staging) ──────────────────────

  const [candidatePalette, setCandidatePalette] =
    useState<ColorPalette>(savedPalette);

  // Sync candidate when saved data first arrives from the server,
  // or after a successful mutation updates the Apollo cache.
  const prevSavedRef = useRef(savedPalette);
  useEffect(() => {
    if (!palettesEqual(savedPalette, prevSavedRef.current)) {
      prevSavedRef.current = savedPalette;
      setCandidatePalette(savedPalette);
    }
  }, [savedPalette]);

  const hasUnsavedChanges = !palettesEqual(candidatePalette, savedPalette);

  // ── Font selections (from server) ─────────────────────────

  const savedHeadingFont = branding?.headingFont ?? null;
  const savedBodyFont = branding?.bodyFont ?? null;

  const [candidateHeadingFont, setCandidateHeadingFont] = useState<string | null>(savedHeadingFont);
  const [candidateBodyFont, setCandidateBodyFont] = useState<string | null>(savedBodyFont);

  // Sync when saved data arrives
  const prevSavedFontsRef = useRef({ h: savedHeadingFont, b: savedBodyFont });
  useEffect(() => {
    if (
      savedHeadingFont !== prevSavedFontsRef.current.h ||
      savedBodyFont !== prevSavedFontsRef.current.b
    ) {
      prevSavedFontsRef.current = { h: savedHeadingFont, b: savedBodyFont };
      setCandidateHeadingFont(savedHeadingFont);
      setCandidateBodyFont(savedBodyFont);
    }
  }, [savedHeadingFont, savedBodyFont]);

  const hasUnsavedFontChanges =
    candidateHeadingFont !== savedHeadingFont ||
    candidateBodyFont !== savedBodyFont;

  // ── Email branding overrides (from server) ─────────────────

  const serverEmailBranding = useMemo<EmailBranding>(() => {
    const raw = branding?.emailBranding;
    if (!raw) return {};
    try {
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
      return {};
    }
  }, [branding]);

  const [emailBranding, setEmailBranding] = useState<EmailBranding>({});

  // Sync when server data arrives
  const prevServerEbRef = useRef(serverEmailBranding);
  useEffect(() => {
    if (JSON.stringify(serverEmailBranding) !== JSON.stringify(prevServerEbRef.current)) {
      prevServerEbRef.current = serverEmailBranding;
      setEmailBranding(serverEmailBranding);
    }
  }, [serverEmailBranding]);

  const hasUnsavedEmailChanges = useMemo(
    () => JSON.stringify(emailBranding) !== JSON.stringify(serverEmailBranding),
    [emailBranding, serverEmailBranding],
  );

  const anyUnsaved = hasUnsavedChanges || hasUnsavedFontChanges || hasUnsavedEmailChanges;

  // ── Candidate helpers ──────────────────────────────────────

  const updateCandidate = useCallback(
    (key: keyof ColorPalette, value: string) => {
      setCandidatePalette((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const applyPreset = useCallback((preset: ThemePreset) => {
    setCandidatePalette({
      primaryColor: preset.colors.primary,
      secondaryColor: preset.colors.secondary,
      tertiaryColor: preset.colors.tertiary,
      backgroundColor: preset.colors.background,
      primaryBgColor: preset.colors.primaryBg,
      secondaryBgColor: preset.colors.secondaryBg,
      tertiaryBgColor: preset.colors.tertiaryBg,
    });
    if (preset.fonts) {
      setCandidateHeadingFont(preset.fonts.heading);
      setCandidateBodyFont(preset.fonts.body);
    }
  }, []);

  const updateEmailOverride = useCallback(
    (key: keyof EmailBranding, value: string | null) => {
      setEmailBranding((prev) => {
        const next = { ...prev };
        if (value == null) {
          delete next[key];
        } else {
          next[key] = value;
        }
        return next;
      });
    },
    [],
  );

  const handleDiscard = useCallback(() => {
    setCandidatePalette(savedPalette);
    setCandidateHeadingFont(savedHeadingFont);
    setCandidateBodyFont(savedBodyFont);
    setEmailBranding(serverEmailBranding);
  }, [savedPalette, savedHeadingFont, savedBodyFont, serverEmailBranding]);

  // ── Apply to live site ─────────────────────────────────────

  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const handleApply = useCallback(async () => {
    setApplying(true);
    setApplyError(null);
    try {
      // Save site palette + font changes
      if (hasUnsavedChanges || hasUnsavedFontChanges) {
        const input: Record<string, string | null> = {};
        if (hasUnsavedChanges) {
          Object.assign(input, candidatePalette);
        }
        if (hasUnsavedFontChanges) {
          input.headingFont = candidateHeadingFont;
          input.bodyFont = candidateBodyFont;
        }
        const result = await updateBranding({
          variables: { input },
        });
        if (result.data?.updateBranding) {
          setCandidatePalette(extractPalette(result.data.updateBranding));
          setCandidateHeadingFont(result.data.updateBranding.headingFont ?? null);
          setCandidateBodyFont(result.data.updateBranding.bodyFont ?? null);
        }
      }
      // Save email branding overrides
      if (hasUnsavedEmailChanges) {
        // Build a clean object: only include keys with non-null values
        const cleanEb: Record<string, string> = {};
        for (const [k, v] of Object.entries(emailBranding)) {
          if (v != null) cleanEb[k] = v;
        }
        await updateBranding({
          variables: {
            input: {
              emailBranding:
                Object.keys(cleanEb).length > 0 ? JSON.stringify(cleanEb) : null,
            },
          },
        });
      }
    } catch (err) {
      setApplyError(
        err instanceof Error ? err.message : "Failed to save changes",
      );
    } finally {
      setApplying(false);
    }
  }, [
    candidatePalette,
    candidateHeadingFont,
    candidateBodyFont,
    emailBranding,
    hasUnsavedChanges,
    hasUnsavedFontChanges,
    hasUnsavedEmailChanges,
    updateBranding,
  ]);

  // ── Publish / discard draft ────────────────────────────────
  //
  // Branding now follows the same draft/publish lifecycle as Pages and
  // Header & Footer (V113). "Save draft" writes the draft row; nothing the
  // tenant does here reaches the live site until Publish copies draft onto
  // published, which also purges the frontend's tenant-config cache.

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [confirmDiscardDraft, setConfirmDiscardDraft] = useState(false);

  const hasUnpublishedChanges = branding?.hasUnpublishedChanges ?? false;

  const handlePublish = useCallback(async () => {
    setPublishing(true);
    setPublishError(null);
    try {
      // Flush any staged palette/font/email edits into the draft first, so
      // Publish never ships a draft that is behind what is on screen.
      if (anyUnsaved) await handleApply();
      await publishBranding();
    } catch (err) {
      setPublishError(
        err instanceof Error ? err.message : "Failed to publish branding",
      );
    } finally {
      setPublishing(false);
    }
  }, [anyUnsaved, handleApply, publishBranding]);

  const handleDiscardDraft = useCallback(async () => {
    setConfirmDiscardDraft(false);
    setPublishing(true);
    setPublishError(null);
    try {
      const result = await revertBrandingDraft();
      const reverted = result.data?.revertBrandingDraft;
      if (reverted) {
        // Re-seed the staged editors from what came back, otherwise the local
        // candidate state would immediately re-dirty the freshly reverted draft.
        setCandidatePalette(extractPalette(reverted));
        setCandidateHeadingFont(reverted.headingFont ?? null);
        setCandidateBodyFont(reverted.bodyFont ?? null);
      }
    } catch (err) {
      setPublishError(
        err instanceof Error ? err.message : "Failed to discard draft",
      );
    } finally {
      setPublishing(false);
    }
  }, [revertBrandingDraft]);

  // ── Image uploads ──────────────────────────────────────────
  //
  // These save straight away rather than staging like the palette does -- an
  // uploaded file is already on the CDN, so there is nothing useful to discard.
  // Since V113 they land on the DRAFT branding row like every other field, so
  // "saved" still means "not live yet"; Publish is what makes them public.
  const handleImageUpdate = useCallback(
    (input: {
      logoUrl?: string | null;
      faviconUrl?: string | null;
      footerLogoUrl?: string | null;
      emailLogoUrl?: string | null;
    }) => {
      void updateBranding({ variables: { input } });
    },
    [updateBranding],
  );

  // ── Unsaved-changes guards ─────────────────────────────────

  // Browser close / reload
  useEffect(() => {
    if (!anyUnsaved) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [anyUnsaved]);

  // React Router in-app navigation
  const blocker = useBlocker(anyUnsaved);

  // ── Derived display values ─────────────────────────────────

  const logoUrl = branding?.logoUrl || null;
  const faviconUrl = branding?.faviconUrl || null;
  const brandName = tenantExtrasData?.currentTenant?.brandName || null;
  const footerLogoUrl = branding?.footerLogoUrl || null;
  const emailLogoUrl = branding?.emailLogoUrl || null;

  // ── Email override resolution (for preview) ────────────────

  const resolvedEmailColors = useMemo(() => {
    const siteBg = candidatePalette.backgroundColor;
    const sitePrimary = candidatePalette.primaryColor;
    const sitePrimaryBg = candidatePalette.primaryBgColor;
    const siteSecondaryBg = candidatePalette.secondaryBgColor;

    return {
      headerBgColor: emailBranding.headerBgColor || siteBg || sitePrimary || "#2663C7",
      accentColor: emailBranding.accentColor || sitePrimary || "#2663C7",
      highlightBgColor: emailBranding.highlightBgColor || sitePrimaryBg || "#f0f8ff",
      calloutBgColor: emailBranding.calloutBgColor || siteSecondaryBg || "#f9f9f9",
    };
  }, [candidatePalette, emailBranding]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-[13px] text-ink-5">Loading branding...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-rust-s/40 px-3 py-2 text-[12.5px] text-rust">
        Failed to load branding: {error.message}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-3">
      {/* Header + tabs */}
      <section className="col-span-12">
        <div className="mb-2">
          <h1 className="font-display text-[40px] font-medium leading-[1.05] tracking-tight">
            Branding
          </h1>
          <p className="mt-2 max-w-xl text-[14.5px] leading-relaxed text-ink-3">
            Customize how your brand appears on your website and in the emails your clients receive.
          </p>
        </div>

        {/* Segmented tab control */}
        <div className="mb-6 inline-flex rounded-[10px] bg-paper-2 p-[3px]">
          <button
            type="button"
            onClick={() => setActiveTab("website")}
            className={`cursor-pointer rounded-[8px] px-5 py-[7px] text-[13px] font-semibold transition ${
              activeTab === "website"
                ? "bg-paper-hi text-ink shadow-sm"
                : "text-ink-4 hover:text-ink-3"
            }`}
          >
            Website
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("email")}
            className={`cursor-pointer rounded-[8px] px-5 py-[7px] text-[13px] font-semibold transition ${
              activeTab === "email"
                ? "bg-paper-hi text-ink shadow-sm"
                : "text-ink-4 hover:text-ink-3"
            }`}
          >
            Email
          </button>
        </div>
      </section>

      {/* ═══════════════════ WEBSITE TAB ═══════════════════ */}
      {activeTab === "website" && (
        <>
          <section className="col-span-12 lg:col-span-8">
            <div className="space-y-8">
              {/* Business name */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Business name
                  </h2>
                </div>
                <div className="p-7">
                  <p className="mb-4 text-[13px] text-ink-3">
                    Your public-facing name. Shown in your site header, footer,
                    and emails whenever a logo is not set.
                  </p>
                  <BusinessNameInput brandName={brandName} />
                </div>
              </section>

              {/* Logo + Favicon side by side */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <section className="shadow-card rounded-lg bg-paper-hi">
                  <div className="hair-b border-b px-7 py-4">
                    <h2 className="text-[14px] font-semibold text-ink">Logo</h2>
                  </div>
                  <div className="p-7">
                    <p className="mb-4 text-[13px] text-ink-3">
                      Used across your website; also the fallback for footer and
                      email logos.
                    </p>
                    <LogoInput
                      logoUrl={logoUrl}
                      onUpdate={(url) => handleImageUpdate({ logoUrl: url })}
                    />
                    {imageUpdateError && (
                      <div className="mt-3 text-[11.5px] text-rust">
                        {imageUpdateError.message}
                      </div>
                    )}
                  </div>
                </section>

                <section className="shadow-card rounded-lg bg-paper-hi">
                  <div className="hair-b border-b px-7 py-4">
                    <h2 className="text-[14px] font-semibold text-ink">
                      Favicon
                    </h2>
                  </div>
                  <div className="p-7">
                    <p className="mb-4 text-[13px] text-ink-3">
                      Browser tabs and bookmarks. We recommend a 32x32 or 48x48
                      PNG, SVG, or ICO.
                    </p>
                    <FaviconInput
                      faviconUrl={faviconUrl}
                      onUpdate={(url) => handleImageUpdate({ faviconUrl: url })}
                    />
                    {imageUpdateError && (
                      <div className="mt-3 text-[11.5px] text-rust">
                        {imageUpdateError.message}
                      </div>
                    )}
                  </div>
                </section>
              </div>

              {/* Footer Logo */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Footer logo
                  </h2>
                </div>
                <div className="p-7">
                  <p className="mb-4 text-[13px] text-ink-3">
                    Optional — your main logo is used if not set.
                  </p>
                  <AlternateLogoInput
                    imageUrl={footerLogoUrl}
                    assetType="footer-logo"
                    label="Footer logo"
                    onUpdate={(url) =>
                      handleImageUpdate({ footerLogoUrl: url })
                    }
                  />
                </div>
              </section>

              {/* Brand Colors */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Brand Colors
                  </h2>
                </div>
                <div className="space-y-8 p-7">
                  <p className="max-w-lg text-[13px] text-ink-3">
                    Your core palette. Primary is used for buttons and links,
                    secondary for supporting accents, and tertiary for deeper
                    emphasis.
                  </p>
                  <ColorInput
                    label="Primary"
                    value={candidatePalette.primaryColor}
                    onChange={(color) => updateCandidate("primaryColor", color)}
                  />
                  <ColorInput
                    label="Secondary"
                    value={candidatePalette.secondaryColor}
                    onChange={(color) =>
                      updateCandidate("secondaryColor", color)
                    }
                  />
                  <ColorInput
                    label="Tertiary"
                    value={candidatePalette.tertiaryColor}
                    onChange={(color) =>
                      updateCandidate("tertiaryColor", color)
                    }
                  />
                </div>
              </section>

              {/* Background Colors */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Background Colors
                  </h2>
                </div>
                <div className="space-y-8 p-7">
                  <p className="max-w-lg text-[13px] text-ink-3">
                    Colors used for section backgrounds and behind your logo.
                    Mix light and dark backgrounds to create visual rhythm
                    across your pages.
                  </p>
                  <ColorInput
                    label="Background"
                    value={candidatePalette.backgroundColor}
                    onChange={(color) =>
                      updateCandidate("backgroundColor", color)
                    }
                  />
                  <ColorInput
                    label="Primary Background"
                    value={candidatePalette.primaryBgColor}
                    onChange={(color) =>
                      updateCandidate("primaryBgColor", color)
                    }
                  />
                  <ColorInput
                    label="Secondary Background"
                    value={candidatePalette.secondaryBgColor}
                    onChange={(color) =>
                      updateCandidate("secondaryBgColor", color)
                    }
                  />
                  <ColorInput
                    label="Tertiary Background"
                    value={candidatePalette.tertiaryBgColor}
                    onChange={(color) =>
                      updateCandidate("tertiaryBgColor", color)
                    }
                  />
                </div>
              </section>

              {/* Typography */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Typography
                  </h2>
                </div>
                <div className="space-y-6 p-7">
                  <p className="max-w-lg text-[13px] text-ink-3">
                    Choose fonts for headings and body text. These apply
                    across your website and page editor.
                  </p>

                  {/* Load Google Fonts for preview */}
                  {(() => {
                    const urls = new Set<string>();
                    const hUrl = getFontUrl(candidateHeadingFont);
                    const bUrl = getFontUrl(candidateBodyFont);
                    if (hUrl) urls.add(hUrl);
                    if (bUrl) urls.add(bUrl);
                    if (urls.size === 0) return null;
                    return (
                      <>
                        {[...urls].map((url) => (
                          <link key={url} rel="stylesheet" href={url} />
                        ))}
                      </>
                    );
                  })()}

                  {/* Heading font */}
                  <div>
                    <label className="mb-1.5 block text-[12.5px] font-medium text-ink-2">
                      Heading font
                    </label>
                    <select
                      value={candidateHeadingFont ?? ""}
                      onChange={(e) =>
                        setCandidateHeadingFont(e.target.value || null)
                      }
                      className="w-full max-w-sm rounded-[9px] border border-ink-6 bg-paper px-[11px] py-2 text-[13px] text-ink transition-all focus:border-tide focus:ring-[3px] focus:ring-tide-s focus:outline-none"
                    >
                      <option value="">Not set (inherits theme)</option>
                      {FONT_STACKS.map((stack) => (
                        <option key={stack.key} value={stack.key}>
                          {stack.label}
                        </option>
                      ))}
                    </select>
                    {candidateHeadingFont && (
                      <div
                        className="daylight mt-3 rounded-md border border-ink-6 bg-paper px-4 py-3"
                        style={{
                          fontFamily: getFontCss(candidateHeadingFont) ?? undefined,
                        }}
                      >
                        <div className="text-[20px] font-semibold text-ink">
                          The quick brown fox jumps
                        </div>
                        <div className="mt-0.5 text-[11px] text-ink-5">
                          {getFontCss(candidateHeadingFont)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Body font */}
                  <div>
                    <label className="mb-1.5 block text-[12.5px] font-medium text-ink-2">
                      Body font
                    </label>
                    <select
                      value={candidateBodyFont ?? ""}
                      onChange={(e) =>
                        setCandidateBodyFont(e.target.value || null)
                      }
                      className="w-full max-w-sm rounded-[9px] border border-ink-6 bg-paper px-[11px] py-2 text-[13px] text-ink transition-all focus:border-tide focus:ring-[3px] focus:ring-tide-s focus:outline-none"
                    >
                      <option value="">Not set (inherits theme)</option>
                      {FONT_STACKS.map((stack) => (
                        <option key={stack.key} value={stack.key}>
                          {stack.label}
                        </option>
                      ))}
                    </select>
                    {candidateBodyFont && (
                      <div
                        className="daylight mt-3 rounded-md border border-ink-6 bg-paper px-4 py-3"
                        style={{
                          fontFamily: getFontCss(candidateBodyFont) ?? undefined,
                        }}
                      >
                        <div className="text-[14px] text-ink leading-relaxed">
                          The quick brown fox jumps over the lazy dog. Pack my
                          box with five dozen liquor jugs. How vexingly quick
                          daft zebras jump.
                        </div>
                        <div className="mt-0.5 text-[11px] text-ink-5">
                          {getFontCss(candidateBodyFont)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Theme Library */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Theme Library
                  </h2>
                </div>
                <div className="p-7">
                  <p className="mb-6 max-w-lg text-[13px] text-ink-3">
                    Start from a professionally designed palette. Applying a
                    theme updates all your brand and background colors at once.
                  </p>
                  <div className="grid grid-cols-2 gap-3 @md:grid-cols-3">
                    {THEME_PRESETS.map((preset) => {
                      const isActive =
                        candidatePalette.primaryColor ===
                          preset.colors.primary &&
                        candidatePalette.secondaryColor ===
                          preset.colors.secondary;
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => applyPreset(preset)}
                          className={`group relative cursor-pointer rounded-lg border bg-paper p-3 text-left transition hover:shadow-card ${
                            isActive
                              ? "border-tide ring-2 ring-tide-s"
                              : "hair hover:border-ink-5"
                          }`}
                        >
                          {isActive && (
                            <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-tide text-white">
                              <Check size={12} strokeWidth={3} />
                            </span>
                          )}
                          <div className="mb-2 flex gap-1">
                            {[
                              preset.colors.primary,
                              preset.colors.secondary,
                              preset.colors.tertiary,
                              preset.colors.background,
                            ].map((c, i) => (
                              <span
                                key={i}
                                className="h-5 flex-1 rounded-[4px]"
                                style={{ backgroundColor: c }}
                              />
                            ))}
                          </div>
                          <div className="text-[12px] font-semibold text-ink-2">
                            {preset.name}
                          </div>
                          <div className="mt-0.5 text-[11px] leading-snug text-ink-4">
                            {preset.description}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              {/* Spacer for sticky action bar */}
              {anyUnsaved && <div className="h-16" />}
            </div>
          </section>

          {/* Website preview rail */}
          <div
            className="col-span-12 lg:col-span-4"
          >
            <div
              className="lg:sticky lg:top-4 overflow-y-auto"
              style={{ maxHeight: "calc(100vh - 120px)" }}
            >
              <BrandingPreview
                logoUrl={logoUrl}
                faviconUrl={faviconUrl}
                primaryColor={candidatePalette.primaryColor}
                secondaryColor={candidatePalette.secondaryColor}
                backgroundColor={candidatePalette.backgroundColor}
                tertiaryColor={candidatePalette.tertiaryColor}
                primaryBgColor={candidatePalette.primaryBgColor}
                secondaryBgColor={candidatePalette.secondaryBgColor}
                tertiaryBgColor={candidatePalette.tertiaryBgColor}
                headingFont={candidateHeadingFont}
                bodyFont={candidateBodyFont}
              />
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════ EMAIL TAB ═══════════════════ */}
      {activeTab === "email" && (
        <>
          <section className="col-span-12 lg:col-span-8">
            <div className="space-y-8">
              {/* Email Logo */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Email logo
                  </h2>
                </div>
                <div className="p-7">
                  <p className="mb-4 text-[13px] text-ink-3">
                    Shown in the header of every customer email. Falls back to
                    your main logo if not set.
                  </p>
                  <AlternateLogoInput
                    imageUrl={emailLogoUrl}
                    assetType="email-logo"
                    label="Email logo"
                    onUpdate={(url) =>
                      handleImageUpdate({ emailLogoUrl: url })
                    }
                  />
                </div>
              </section>

              {/* Header Background */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Header background
                  </h2>
                </div>
                <div className="p-7">
                  <p className="text-[13px] text-ink-3">
                    The colored band behind your logo at the top of emails.
                  </p>
                  <InheritToggle
                    inheritLabel="Background"
                    inheritedColor={candidatePalette.backgroundColor}
                    defaultColor={candidatePalette.primaryColor || "#2663C7"}
                    customValue={emailBranding.headerBgColor ?? null}
                    onChange={(v) => updateEmailOverride("headerBgColor", v)}
                  />
                </div>
              </section>

              {/* Accent / Buttons */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Accent and buttons
                  </h2>
                </div>
                <div className="p-7">
                  <p className="text-[13px] text-ink-3">
                    Buttons, links, and highlight borders inside email bodies.
                  </p>
                  <InheritToggle
                    inheritLabel="Primary"
                    inheritedColor={candidatePalette.primaryColor}
                    defaultColor="#2663C7"
                    customValue={emailBranding.accentColor ?? null}
                    onChange={(v) => updateEmailOverride("accentColor", v)}
                  />
                </div>
              </section>

              {/* Content Boxes */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Content boxes
                  </h2>
                </div>
                <div className="p-7">
                  <p className="text-[13px] text-ink-3">
                    Backgrounds for highlight and callout boxes in email bodies.
                  </p>
                  <div className="mt-4">
                    <div className="text-[12.5px] font-medium text-ink-2">
                      Highlight box
                    </div>
                    <InheritToggle
                      inheritLabel="Primary Background"
                      inheritedColor={candidatePalette.primaryBgColor}
                      defaultColor="#f0f8ff"
                      customValue={emailBranding.highlightBgColor ?? null}
                      onChange={(v) =>
                        updateEmailOverride("highlightBgColor", v)
                      }
                    />
                  </div>
                  <div className="mt-6">
                    <div className="text-[12.5px] font-medium text-ink-2">
                      Callout box
                    </div>
                    <InheritToggle
                      inheritLabel="Secondary Background"
                      inheritedColor={candidatePalette.secondaryBgColor}
                      defaultColor="#f9f9f9"
                      customValue={emailBranding.calloutBgColor ?? null}
                      onChange={(v) =>
                        updateEmailOverride("calloutBgColor", v)
                      }
                    />
                  </div>
                </div>
              </section>

              {/* Wordmark Fallback */}
              <section className="shadow-card rounded-lg bg-paper-hi">
                <div className="hair-b border-b px-7 py-4">
                  <h2 className="text-[14px] font-semibold text-ink">
                    Wordmark fallback
                  </h2>
                </div>
                <div className="p-7">
                  <p className="text-[13px] text-ink-4">
                    If no logo is set anywhere, emails show your brand name as
                    styled text. This is already live; nothing to configure here.
                  </p>
                </div>
              </section>

              {/* Spacer for sticky action bar */}
              {anyUnsaved && <div className="h-16" />}
            </div>
          </section>

          {/* Email preview rail */}
          <div
            className="col-span-12 lg:col-span-4"
          >
            <div
              className="lg:sticky lg:top-4 overflow-y-auto"
              style={{ maxHeight: "calc(100vh - 120px)" }}
            >
              <EmailPreview
                logoUrl={logoUrl}
                emailLogoUrl={emailLogoUrl}
                headerBgColor={resolvedEmailColors.headerBgColor}
                accentColor={resolvedEmailColors.accentColor}
                highlightBgColor={resolvedEmailColors.highlightBgColor}
                calloutBgColor={resolvedEmailColors.calloutBgColor}
              />
            </div>
          </div>
        </>
      )}

      {/* ── Sticky action bar ─────────────────────────────── */}
      {(anyUnsaved || hasUnpublishedChanges) && (
        <div className="col-span-12 sticky bottom-0 z-30">
          <div className="hair-t -mx-1 border-t bg-paper-hi/95 px-7 py-3 shadow-lift backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-[13px] font-medium text-ink-3">
                {/* Same two tones the page editor uses for these states -- see
                    page-canvas/EditorTopBar.tsx. */}
                <span
                  className={`inline-flex items-center rounded-full px-2 py-[2px] text-[11px] font-semibold ${
                    anyUnsaved
                      ? "bg-spark/10 text-spark border border-spark/20"
                      : "bg-[#f0e6cf] text-[#7a6230]"
                  }`}
                >
                  {anyUnsaved ? "Unsaved changes" : "Draft changes"}
                </span>
                {anyUnsaved
                  ? "Save your draft to keep these edits."
                  : "Your draft differs from what is live."}
              </span>
              <div className="flex items-center gap-3">
                {anyUnsaved && (
                  <button
                    type="button"
                    onClick={handleDiscard}
                    disabled={applying || publishing}
                    className="cursor-pointer text-[13px] font-semibold text-ink-3 transition-colors hover:text-ink disabled:opacity-45"
                  >
                    Discard edits
                  </button>
                )}
                {!anyUnsaved && hasUnpublishedChanges && (
                  <button
                    type="button"
                    onClick={() => setConfirmDiscardDraft(true)}
                    disabled={applying || publishing}
                    className="cursor-pointer text-[13px] font-semibold text-ink-3 transition-colors hover:text-ink disabled:opacity-45"
                  >
                    Discard draft
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={applying || publishing || !anyUnsaved}
                  className="hair cursor-pointer rounded-[10px] border px-4 py-2 text-[13px] font-semibold text-ink-2 transition-colors hover:bg-paper-2 disabled:opacity-45"
                >
                  {applying ? "Saving..." : "Save draft"}
                </button>
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={applying || publishing}
                  className="cursor-pointer rounded-[10px] border border-tide bg-tide px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-tide-d disabled:opacity-45"
                >
                  {publishing ? "Publishing..." : "Publish"}
                </button>
              </div>
            </div>
            {(applyError || publishError) && (
              <div className="mt-2 text-[12px] text-rust">
                {applyError || publishError}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Discard-draft confirmation */}
      <ConfirmDialog
        open={confirmDiscardDraft}
        title="Discard draft branding?"
        description="Your unpublished branding changes will be replaced with what is currently live. This cannot be undone."
        variant="danger"
        confirmLabel="Discard draft"
        cancelLabel="Keep draft"
        onConfirm={() => void handleDiscardDraft()}
        onCancel={() => setConfirmDiscardDraft(false)}
      />

      {/* React Router blocker confirmation dialog */}
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title="Unsaved changes"
        description="You have unsaved color changes that will be lost if you leave this page."
        variant="danger"
        confirmLabel="Leave"
        cancelLabel="Stay"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </div>
  );
}
